import { useEffect, useRef } from 'react'
import { useSearchParams } from 'react-router'

import { type User, useSwitchOrg } from '@/models/user'

/**
 * A link to the page names the business it's for, so someone in several opens that one's
 * application. True while switching to it; the org leaves the URL once it's the current one.
 */
export function useLinkedOrg(user: User | undefined): boolean {
  const [searchParams, setSearchParams] = useSearchParams()
  const linkedOrgId = searchParams.get('org')
  const switchOrg = useSwitchOrg()
  const requested = useRef<string | null>(null)
  const switching = !!user && !!linkedOrgId && linkedOrgId !== user.currentOrg.id

  useEffect(() => {
    if (!user || !linkedOrgId) return
    const dropOrg = () =>
      setSearchParams(
        (params) => {
          params.delete('org')
          return params
        },
        { replace: true }
      )
    if (linkedOrgId === user.currentOrg.id) return dropOrg()
    if (requested.current === linkedOrgId) return
    requested.current = linkedOrgId
    // A business they aren't in is refused, and they stay on their current one.
    switchOrg.mutate(linkedOrgId, { onError: dropOrg })
  }, [user, linkedOrgId, switchOrg, setSearchParams])

  return switching
}
